/**
 * PWA Components
 *
 * Components for Progressive Web App functionality:
 * - InstallPrompt: Prompts user to install the app
 * - OfflineIndicator: Shows offline status and pending sync count
 * - UpdatePrompt: Notifies user when app update is available
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getTracer } from "@/telemetry";

const INSTALL_DISMISS_KEY = "pwa-install-dismissed-at";
const INSTALL_DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const AUTO_UPDATE_ROUTE_BLOCKLIST = ["/check-in", "/ai-check-in", "/daily", "/settings", "/events/new"];

function hasFocusedEditableElement(): boolean {
  const activeElement = document.activeElement;
  if (!(activeElement instanceof HTMLElement)) {
    return false;
  }

  const tagName = activeElement.tagName;
  if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
    return true;
  }

  if (activeElement.isContentEditable) {
    return true;
  }

  return activeElement.closest("form") !== null;
}

function canAutoApplyUpdateOnHide(pathname: string): boolean {
  if (AUTO_UPDATE_ROUTE_BLOCKLIST.some((blockedRoute) => pathname.startsWith(blockedRoute))) {
    return false;
  }

  return !hasFocusedEditableElement();
}

function isStandaloneMode(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // @ts-expect-error - Safari-specific property
    window.navigator.standalone === true
  );
}

function isMobileViewport(): boolean {
  return window.matchMedia("(max-width: 1023px)").matches;
}

function isIOSDevice(): boolean {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

type InstallPromptSurface = "native" | "ios_manual";

function trackInstallPromptEvent(eventName: string, attributes: Record<string, string | boolean> = {}): void {
  const tracer = getTracer("floline-web.pwa");
  const span = tracer.startSpan("ui.pwa.install_prompt");
  span.setAttributes({
    "pwa.event": eventName,
    ...attributes,
  });
  span.end();
}

// ============================================================================
// Types
// ============================================================================

/**
 * BeforeInstallPromptEvent interface
 * @see https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

/**
 * Props for OfflineIndicator
 */
export interface OfflineIndicatorProps {
  /** Whether the device is online */
  isOnline: boolean;
  /** Number of pending operations to sync */
  pendingCount?: number;
  /** Whether a sync is currently in progress */
  isSyncing?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for UpdatePrompt
 */
export interface UpdatePromptProps {
  /** Custom class name */
  className?: string;
}

// ============================================================================
// Icon Components (with proper accessibility)
// ============================================================================

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function OfflineIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
      />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

// ============================================================================
// Install Prompt Component
// ============================================================================

/**
 * PWA install prompt that appears when the app can be installed.
 * Shows a dismissible banner with an install button.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <>
 *       <InstallPrompt />
 *       <main>...</main>
 *     </>
 *   );
 * }
 * ```
 */
export function InstallPrompt() {
  if (import.meta.env.DEV) {
    return null;
  }

  const { t } = useTranslation("common");

  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const shownForSurfaceRef = useRef<InstallPromptSurface | null>(null);

  // Check installation/mobile state and previous dismissals.
  useEffect(() => {
    const updateDeviceState = () => {
      setIsMobile(isMobileViewport());
      setIsIOS(isIOSDevice());
    };

    updateDeviceState();
    window.addEventListener("resize", updateDeviceState);

    if (isStandaloneMode()) {
      setIsInstalled(true);
    }

    const dismissedAtRaw = localStorage.getItem(INSTALL_DISMISS_KEY);
    const dismissedAt = dismissedAtRaw ? Number(dismissedAtRaw) : Number.NaN;
    const isDismissalActive = Number.isFinite(dismissedAt) && Date.now() - dismissedAt < INSTALL_DISMISS_TTL_MS;
    if (isDismissalActive) {
      setIsDismissed(true);
    } else if (dismissedAtRaw) {
      localStorage.removeItem(INSTALL_DISMISS_KEY);
    }

    return () => {
      window.removeEventListener("resize", updateDeviceState);
    };
  }, []);

  // Listen for install prompt event
  useEffect(() => {
    function handleBeforeInstallPrompt(e: Event) {
      // Prevent Chrome's default mini-infobar
      e.preventDefault();
      // Save the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      trackInstallPromptEvent("eligible", {
        "pwa.install_surface": "native",
      });
    }

    function handleAppInstalled() {
      setIsInstalled(true);
      setDeferredPrompt(null);
      trackInstallPromptEvent("installed");
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    trackInstallPromptEvent("install_clicked", {
      "pwa.install_surface": "native",
    });

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setIsInstalled(true);
      trackInstallPromptEvent("accepted", {
        "pwa.install_surface": "native",
      });
    } else {
      trackInstallPromptEvent("browser_dismissed", {
        "pwa.install_surface": "native",
      });
    }

    // Clear the prompt - can only be used once
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()));
    trackInstallPromptEvent("dismissed", {
      "pwa.install_surface": isIOS && !deferredPrompt ? "ios_manual" : "native",
    });
  }, [deferredPrompt, isIOS]);

  const showManualIOSPrompt = isIOS && !deferredPrompt;
  const canShowPrompt = isMobile && !isInstalled && !isDismissed && (deferredPrompt || showManualIOSPrompt);

  useEffect(() => {
    if (!canShowPrompt) {
      shownForSurfaceRef.current = null;
      return;
    }

    const surface: InstallPromptSurface = showManualIOSPrompt ? "ios_manual" : "native";
    if (shownForSurfaceRef.current === surface) {
      return;
    }

    trackInstallPromptEvent("shown", {
      "pwa.install_surface": surface,
    });
    shownForSurfaceRef.current = surface;
  }, [canShowPrompt, showManualIOSPrompt]);

  if (!canShowPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 mx-auto max-w-md animate-in slide-in-from-bottom-4 duration-300 lg:bottom-4">
      <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-4 shadow-lg">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <DownloadIcon className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{t("pwa.installTitleMobile")}</p>
          <p className="text-xs text-muted-foreground">
            {t("pwa.installDescriptionMobile")}
          </p>
          {showManualIOSPrompt && (
            <p className="pt-1 text-xs text-muted-foreground">{t("pwa.installIosHint")}</p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            {t("actions.later")}
          </Button>
          {deferredPrompt && (
            <Button size="sm" onClick={handleInstall}>
              {t("actions.install")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Offline Indicator Component
// ============================================================================

/**
 * Shows connection status and pending sync operations.
 * Appears as a small banner when offline or syncing.
 *
 * @example
 * ```tsx
 * function App() {
 *   const { isOnline, stats, isSyncing } = useOfflineQueue();
 *
 *   return (
 *     <>
 *       <OfflineIndicator
 *         isOnline={isOnline}
 *         pendingCount={stats.pendingCount}
 *         isSyncing={isSyncing}
 *       />
 *       <main>...</main>
 *     </>
 *   );
 * }
 * ```
 */
export function OfflineIndicator({
  isOnline,
  pendingCount = 0,
  isSyncing = false,
  className,
}: OfflineIndicatorProps) {
  const { t } = useTranslation("common");

  // Don't show if online with no pending items
  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null;
  }

  const statusText = isOnline
    ? isSyncing
      ? t("pwa.statusSyncing")
      : t("pwa.statusPending", { count: pendingCount })
    : pendingCount > 0
      ? t("pwa.statusOfflineWillSync", { count: pendingCount })
      : t("pwa.statusOffline");

  return (
    <output
      className={cn(
        "fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 py-1.5 px-4 text-xs font-medium transition-colors",
        isOnline
          ? "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
          : "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-100",
        className
      )}
      aria-live="polite"
    >
      {/* Status icon */}
      {isOnline ? (
        isSyncing ? (
          <SpinnerIcon className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <ClockIcon className="h-3.5 w-3.5" />
        )
      ) : (
        <OfflineIcon className="h-3.5 w-3.5" />
      )}

      {/* Status text */}
      <span>{statusText}</span>
    </output>
  );
}

// ============================================================================
// Update Prompt Component
// ============================================================================

/**
 * Notifies user when a new version of the app is available.
 * Uses the PWA service worker update mechanism.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <>
 *       <UpdatePrompt />
 *       <main>...</main>
 *     </>
 *   );
 * }
 * ```
 */
export function UpdatePrompt({ className }: UpdatePromptProps) {
  if (import.meta.env.DEV) {
    return null;
  }

  const { t } = useTranslation("common");

  const UPDATE_SNOOZE_MS = 10 * 60 * 1000;
  const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

  // Ref to track the SW update-check interval so we can clean it up.
  const updateIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const [updateSnoozedUntil, setUpdateSnoozedUntil] = useState<number | null>(null);

  const checkForSwUpdate = useCallback(async (swUrl: string, registration: ServiceWorkerRegistration) => {
    if (registration.installing || !navigator.onLine) {
      return;
    }

    try {
      const response = await fetch(swUrl, {
        cache: "no-store",
        headers: {
          "cache-control": "no-cache",
        },
      });

      if (response.status === 200) {
        await registration.update();
      }
    } catch (error: unknown) {
      console.warn("SW update check failed:", error);
    }
  }, []);

  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      if (!swUrl || !registration) {
        return;
      }

      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }

      updateIntervalRef.current = setInterval(() => {
        void checkForSwUpdate(swUrl, registration);
      }, UPDATE_CHECK_INTERVAL_MS);

      const triggerCheck = () => {
        void checkForSwUpdate(swUrl, registration);
      };

      window.addEventListener("focus", triggerCheck);
      window.addEventListener("online", triggerCheck);
      document.addEventListener("visibilitychange", triggerCheck);

      return () => {
        window.removeEventListener("focus", triggerCheck);
        window.removeEventListener("online", triggerCheck);
        document.removeEventListener("visibilitychange", triggerCheck);
      };
    },
    onRegisterError(error) {
      console.error("SW registration error:", error);
    },
  });

  // Clean up the periodic update-check interval on unmount.
  useEffect(() => {
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = undefined;
      }
    };
  }, []);

  const handleUpdate = useCallback(() => {
    updateServiceWorker(true);
  }, [updateServiceWorker]);

  const handleClose = useCallback(() => {
    if (needRefresh) {
      setUpdateSnoozedUntil(Date.now() + UPDATE_SNOOZE_MS);
      return;
    }

    setOfflineReady(false);
  }, [needRefresh, setOfflineReady]);

  useEffect(() => {
    if (!needRefresh) {
      setUpdateSnoozedUntil(null);
      return;
    }

    const applyWhenHidden = () => {
      if (document.visibilityState !== "hidden") {
        return;
      }

      if (!canAutoApplyUpdateOnHide(window.location.pathname)) {
        return;
      }

      void updateServiceWorker(true);
    };

    applyWhenHidden();
    document.addEventListener("visibilitychange", applyWhenHidden);
    return () => {
      document.removeEventListener("visibilitychange", applyWhenHidden);
    };
  }, [needRefresh, updateServiceWorker]);

  useEffect(() => {
    if (!needRefresh || updateSnoozedUntil === null) {
      return;
    }

    const remaining = updateSnoozedUntil - Date.now();
    if (remaining <= 0) {
      setUpdateSnoozedUntil(null);
      return;
    }

    const timer = setTimeout(() => {
      setUpdateSnoozedUntil(null);
    }, remaining);

    return () => clearTimeout(timer);
  }, [needRefresh, updateSnoozedUntil]);

  const showNeedRefresh = needRefresh && (updateSnoozedUntil === null || Date.now() >= updateSnoozedUntil);

  // Show offline ready toast briefly
  useEffect(() => {
    if (offlineReady) {
      const timer = setTimeout(() => {
        setOfflineReady(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [offlineReady, setOfflineReady]);

  // Don't show if nothing to display
  if (!showNeedRefresh && !offlineReady) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md animate-in slide-in-from-bottom-4 duration-300",
        className
      )}
    >
      <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-4 shadow-lg">
        {showNeedRefresh ? (
          <>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <RefreshIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{t("pwa.updateAvailableTitle")}</p>
              <p className="text-xs text-muted-foreground">
                {t("pwa.updateAvailableDescription")}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={handleClose}>
                {t("actions.later")}
              </Button>
              <Button size="sm" onClick={handleUpdate}>
                {t("actions.update")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
              <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{t("pwa.offlineReadyTitle")}</p>
              <p className="text-xs text-muted-foreground">
                {t("pwa.offlineReadyDescription")}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              {t("actions.dismiss")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
